/**
 * transforms/connectorPlacement.ts — SSOT del posizionamento locale di
 * joist/header (posizione + quaternion, RELATIVI al proprio parent chain:
 * `joistPivot` per il joist, `headerChain` per l'header — vedi
 * `model/connectors.ts:placeConnectorGroup`).
 *
 * Consolida in UN SOLO punto le trasformazioni che prima dell'indagine
 * incastro 25-09-2026 erano composte inline in `connectors.ts` (flush X,
 * mirror G3, conversione autoring→viewer): qualunque ulteriore aggiustamento
 * di segno/asse va fatto QUI, non in tre punti diversi. Funzione pura,
 * testabile senza scene graph three.js (nessun `Object3D`, solo
 * `THREE.Quaternion`/`Vector3` per l'algebra, come da regola SPEC §6 per
 * `transforms/`: e' consentito three.js per la sola matematica vettoriale).
 *
 * ORIENTAMENTO (corretto 25-09-2026 — due iterazioni sul segno: la prima,
 * "correzione universale" su entrambi i lati, era sbagliata; la seconda,
 * componendo `rz180` DOPO `authoringToViewerQuatForHeader()`, produceva un
 * header con l'asse spessore rivolto nella STESSA direzione mondiale del
 * joist invece che opposta — risolto rimuovendo quella composizione):
 * - JOIST: `authoringToViewerQuat()` = Ry(-90°), PIU' un flip mondiale
 *   Rx180 (vedi sotto) — nessun'altra correzione: i dati `features.json`
 *   sono la verita' diretta per questo lato (confermato: fori/viti allineati
 *   usando SOLO questa composizione).
 * - HEADER: `authoringToViewerQuatForHeader()` = Ry(+90°) **da sola**, SENZA
 *   comporre un ulteriore Rz180. Segno OPPOSTO al joist — necessario, essendo
 *   header sul lato opposto del piano condiviso — mantenendo invariato l'asse
 *   verticale (Y, alto/basso): comporre anche Rz180 ribalta INVECE anche Y,
 *   capovolgendo l'header sottosopra (errore trovato in un tentativo precedente).
 *
 * NOTA (verificato con calcolo reale 25-09-2026, non a mano): con questa
 * composizione e' l'asse Z locale dell'HEADER — non del joist — a puntare
 * come l'asse X del piano globale (`plane.normal`); il joist punta verso
 * -X. Un vecchio commento (basato solo sui tripodi di debug, prima
 * dell'aggiunta del flip Rx180 sul joist) affermava l'opposto: non e' un
 * bug, l'unico criterio che conta e' il risultato VISIVO finale confermato
 * dall'utente (viti nei fori, connettori flush — vedi
 * `model/connectorGoodConfig.test.ts`, che verifica solo l'invariante
 * RELATIVO fra joist e header, non una direzione assoluta arbitraria).
 *
 * Posizione X: flush sul piano trave (non centrato) — il modulo si estende
 * INTERAMENTE fra il piano connettore e la trave, verso l'interno
 * (confermato dall'utente 25-09-2026). Posizione Y: formula G5/C1
 * (`connectorFlush.ts`, gia' isolata li'). Posizione Z: `zCenter` (G9).*/

import * as THREE from "three";
import type { GlbFeatures, Vec3 } from "../data/types";
import { applyAuthoringToViewer, authoringToViewerQuat, authoringToViewerQuatForHeader } from "./authoringToViewer";
import { resolveConnectorHeights, type ConnectorFlushResult } from "./connectorFlush";
import { axisAngleQuat } from "./vectors";
import { JOIST_WORLD_X_FLIP_DEG } from "../config/defaults";

/** Placement locale (posizione + quaternion) di UN lato (joist o header).*/
export interface ConnectorFramePlacement {
  position: Vec3;
  quaternion: THREE.Quaternion;
}

/** Risultato completo: placement di entrambi i lati + il flush Y (riusato per `ConnectorInstance.offsetY`).*/
export interface ConnectorPlacementResult {
  joist: ConnectorFramePlacement;
  header: ConnectorFramePlacement;
  flush: ConnectorFlushResult;
}

/**
 * Calcola posizione+quaternion LOCALI di joist e header per un singolo
 * connettore (un `GlbFeatures`, SSOT geometria — dati autoritativi lato
 * "joist"; l'header e' una derivazione/mirror dello stesso asset, SPEC G3).
 *
 * @param features Features SSOT del connettore (lato joist).
 * @param zCenter Z del centro di QUESTO connettore (G9, gia' calcolato da `nConnectorsOffsetsZ`).
 * @param pivotOffsetY Offset Y del pivot rispetto alla root (SPEC G6, per l'header).
 * @param joistNotchDownwardShiftMm Traslazione verso il basso quando notch+angolo
 * sono attivi (01-10-2026, richiesta utente) — vedi doc-comment sul parametro
 * omonimo di `connectorFlush.ts:resolveConnectorHeights`, da cui viene SOLO
 * applicata qui, non ricalcolata. Default 0.
 * @returns Placement locali di joist/header + il risultato flush G5/C1.*/
export function resolveConnectorPlacement(
  features: GlbFeatures,
  zCenter: number,
  pivotOffsetY: number,
  joistNotchDownwardShiftMm = 0,
): ConnectorPlacementResult {
  const flush = resolveConnectorHeights(
    features.connector_module.height,
    features.connector_assembly.height,
    joistNotchDownwardShiftMm,
  );

  //─── JOIST — Ry(-90°) + flip mondiale Rx180 (sotto) ──────────────────────
  //TEST (25-09-2026, richiesta utente): flush X rimosso del tutto — nessun
  //±module.thickness/2, solo moduleOffset[0] (di norma 0). Verificare con
  //dati reali dopo il reload dove finisce il connettore senza questo termine.
  const moduleOffset = applyAuthoringToViewer(features.connector_module.position);
  const joistPosition: Vec3 = [moduleOffset[0], flush.joistCenterOffsetY, zCenter];
  //TEST (25-09-2026, richiesta utente): manca una rotazione di 180° del joist
  //rispetto all'asse X GLOBALE (del piano, non locale/autoring) — applicata
  //OUTERMOST (world = Rx180_globale * authoringToViewerQuat() * local), non
  //prima della conversione come le correzioni precedenti (quelle erano nel
  //frame di autoring, questa e' nel frame mondo).
  const joistQuaternion = axisAngleQuat([1, 0, 0], JOIST_WORLD_X_FLIP_DEG).multiply(authoringToViewerQuat());

  //─── HEADER (mirror G3) — Ry(+90°), segno opposto al joist, DA SOLA ──────
  //Posizione modulo header: mirror in autoring (Rz180) PRIMA della conversione
  //(stessa semantica della vecchia deriveHeaderFeatures — le features SORGENTE
  //non vengono negate, solo la posizione derivata qui).
  const headerModulePos: Vec3 = [
    -features.connector_module.position[0],
    -features.connector_module.position[1],
    features.connector_module.position[2],
  ];
  const headerModuleOffset = applyAuthoringToViewer(headerModulePos);
  const headerPosition: Vec3 = [
    headerModuleOffset[0],
    pivotOffsetY + flush.headerCenterOffsetY + headerModuleOffset[1],
    zCenter + headerModuleOffset[2],
  ];
  //world = headerConversionQuat(Ry+90°) * local — NESSUN Rz180 aggiuntivo
  //(vedi doc-comment in cima al file per il perche').
  const headerQuaternion = authoringToViewerQuatForHeader();

  return {
    joist: { position: joistPosition, quaternion: joistQuaternion },
    header: { position: headerPosition, quaternion: headerQuaternion },
    flush,
  };
}
